export const USER_PREFERENCES_STORAGE_KEY = 'way_user_preferences';

export interface UserPreferences {
  /** When true, the desktop sidebar shows icon-only (collapsed) rail. */
  sidebarCollapsed?: boolean;
  /** When true, General Sales Report exports include the cancelled-order list. */
  gsrIncludeCancelledOrders?: boolean;
}

interface StoredUserEntry {
  preferences: UserPreferences;
}

export interface UserPreferencesStore {
  users: Record<string, StoredUserEntry>;
}

function emptyStore(): UserPreferencesStore {
  return { users: {} };
}

function readStore(): UserPreferencesStore {
  if (typeof window === 'undefined') {
    return emptyStore();
  }
  try {
    const raw = localStorage.getItem(USER_PREFERENCES_STORAGE_KEY);
    if (!raw) {
      return emptyStore();
    }
    const parsed = JSON.parse(raw) as UserPreferencesStore;
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      !parsed.users ||
      typeof parsed.users !== 'object'
    ) {
      return emptyStore();
    }
    return parsed;
  } catch {
    return emptyStore();
  }
}

function writeStore(store: UserPreferencesStore): void {
  if (typeof window === 'undefined') {
    return;
  }
  localStorage.setItem(USER_PREFERENCES_STORAGE_KEY, JSON.stringify(store));
}

/** Resolve the active session user id from persisted auth payloads. */
export function resolvePreferenceUserId(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  for (const key of ['userData', 'superAdminData'] as const) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) {
        continue;
      }
      const parsed = JSON.parse(raw);
      const id = parsed?.id ?? parsed?.user_id ?? parsed?.userId;
      if (id != null && String(id).trim() !== '') {
        return String(id).trim();
      }
    } catch {
      // ignore malformed auth blobs
    }
  }
  return null;
}

export function getSidebarCollapsedPreference(userId: string): boolean | null {
  const normalized = String(userId ?? '').trim();
  if (!normalized) {
    return null;
  }
  const entry = readStore().users[normalized]?.preferences;
  return typeof entry?.sidebarCollapsed === 'boolean' ? entry.sidebarCollapsed : null;
}

export function getGsrIncludeCancelledOrdersPreference(userId: string): boolean | null {
  const normalized = String(userId ?? '').trim();
  if (!normalized) {
    return null;
  }
  const entry = readStore().users[normalized]?.preferences;
  return typeof entry?.gsrIncludeCancelledOrders === 'boolean'
    ? entry.gsrIncludeCancelledOrders
    : null;
}

export function setGsrIncludeCancelledOrdersPreference(userId: string, include: boolean): void {
  const normalized = String(userId ?? '').trim();
  if (!normalized) {
    return;
  }
  const store = readStore();
  const existing = store.users[normalized]?.preferences ?? {};
  store.users[normalized] = {
    preferences: {
      ...existing,
      gsrIncludeCancelledOrders: include,
    },
  };
  writeStore(store);
}

export function setSidebarCollapsedPreference(userId: string, collapsed: boolean): void {
  const normalized = String(userId ?? '').trim();
  if (!normalized) {
    return;
  }
  const store = readStore();
  const existing = store.users[normalized]?.preferences ?? {};
  store.users[normalized] = {
    preferences: {
      ...existing,
      sidebarCollapsed: collapsed,
    },
  };
  writeStore(store);
}
